import { DateTime } from 'luxon';
import {
  SupportTicket,
  SupportTicketStatus,
  SupportTicketType,
  formatDateTimeShort,
  ticketTypeLabel,
  lightTheme,
} from '@vgrubs/components';

/** Names, colours and tabs for the support tickets list. */

export const TICKET_TYPE_OPTIONS: Array<{
  value: SupportTicketType;
  label: string;
}> = [
  { value: 'orderCancel', label: 'Cancel order' },
  { value: 'orderAdjustment', label: 'Order adjustment' },
  { value: 'refund', label: 'Refund' },
  { value: 'missingItem', label: 'Missing item' },
  { value: 'payout', label: 'Payout' },
  { value: 'menuIssue', label: 'Menu issue' },
  { value: 'other', label: 'Other' },
];

/**
 * The four views of the same list.
 *
 * Every one of them is a server-side filter rather than a slice of one fetched
 * list, because the sort that matters (oldest waiting first, within status)
 * only exists server side - and "Mine" needs the reader's own id, which the
 * ticket itself does not carry.
 */
export type SupportTicketTab = 'open' | 'mine' | 'progress' | 'resolved';

export const SUPPORT_TICKET_TABS: Array<{
  value: SupportTicketTab;
  label: string;
}> = [
  { value: 'open', label: 'Open' },
  { value: 'mine', label: 'Mine' },
  { value: 'progress', label: 'In progress' },
  { value: 'resolved', label: 'Resolved' },
];

/** What a tab asks the API for. `claimedById` is only ever the reader's own. */
export const tabFilters = (
  tab: SupportTicketTab,
  viewerId: number
): { status: SupportTicketStatus; claimedById?: number } => {
  switch (tab) {
    case 'mine':
      return { status: 'claimed', claimedById: viewerId };
    case 'progress':
      return { status: 'claimed' };
    case 'resolved':
      return { status: 'resolved' };
    default:
      return { status: 'open' };
  }
};

/**
 * The status pill's fill. White text on a solid colour, the way every other
 * status in the admin is drawn - see `DeliveryStatusPill`.
 *
 * Same three colours the delivery timeline uses for the same three ideas:
 * waiting, being worked on, done.
 */
export const statusPillColor = (status: SupportTicketStatus) => {
  if (status === 'open') return lightTheme.yellow;
  if (status === 'claimed') return lightTheme.darkBlue;
  return lightTheme.green;
};

/**
 * How long a ticket has been waiting, spelled out.
 *
 * The chat's `formatRelativeTime` is deliberately terse ("4d") because it sits
 * at the end of an inbox row where space is the constraint. Here the age is the
 * reason the list is in this order, so it gets whole words - and past a week it
 * becomes a date, because "13 days ago" is a number nobody counts back from.
 */
export const formatTicketAge = (iso: string) => {
  const opened = DateTime.fromISO(iso);
  const diff = DateTime.now().diff(opened);

  const minutes = Math.floor(diff.as('minutes'));
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;

  const hours = Math.floor(diff.as('hours'));
  if (hours < 24) return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;

  const days = Math.floor(diff.as('days'));
  if (days < 7) return `${days} ${days === 1 ? 'day' : 'days'} ago`;

  return opened.toFormat('MMM d');
};

/**
 * When a ticket was opened, for the detail: the exact time, and how long ago
 * that was while "ago" still means something. Past a week `formatTicketAge`
 * is itself a date, and printing the date twice says nothing the once did not.
 */
export const formatTicketOpened = (iso: string) => {
  const exact = formatDateTimeShort(iso);
  const days = DateTime.now().diff(DateTime.fromISO(iso)).as('days');
  return days < 7 ? `${exact} (${formatTicketAge(iso)})` : exact;
};

/**
 * Whether this reader may resolve the ticket.
 *
 * Only the person who claimed it can, which the server enforces - so the
 * button is offered on exactly that condition rather than left to be refused.
 * Handing a ticket back is deliberately NOT restricted this way: that is how a
 * colleague's ticket is taken over when their shift ends.
 */
export const canResolveTicket = (ticket: SupportTicket, viewerId: number) =>
  ticket.status === 'claimed' && ticket.claimedById === viewerId;

/**
 * Whether this reader may write to the merchant from the ticket.
 *
 * Resolve's rule plus open tickets, since a reply claims an open ticket for
 * whoever sends it. A ticket somebody else holds stays theirs to answer.
 */
export const canReplyTicket = (ticket: SupportTicket, viewerId: number) =>
  ticket.status === 'open' || canResolveTicket(ticket, viewerId);

/**
 * The merchant's messages on a ticket, one string each.
 *
 * The backend keeps them as one text and appends a later message under a
 * blank line, so a blank line is the only seam there is. Single newlines stay
 * inside a message.
 */
export const ticketMessages = (details: string) =>
  details.split(/\n{2,}/).filter((message) => message.trim() !== '');

const squash = (text: string) => text.replace(/\s+/g, ' ').trim().toLowerCase();

/**
 * Whether the bot's subject says anything the type does not.
 *
 * In practice it is mostly the category again ("Refund", "Payout"), and a
 * heading that repeats the header's own subtitle reads as a mistake - so the
 * detail shows it only when it adds something.
 */
export const subjectAddsToType = (ticket: SupportTicket) =>
  squash(ticket.subject) !== squash(ticketTypeLabel(ticket.type));
